import { useTranslation } from "react-i18next";
import { Control, Controller } from "react-hook-form";
import { FormControl, FormLabel, TextField, Autocomplete, FormHelperText } from "@mui/material";

import { useTags } from "../../api/queries/useTags.query";
import { CreatePostAutocompleteIcon } from "../../assets/icons/create-feed";

import { CreatePostFormData } from "../../api/types/feed.types";
import { commonStyles } from "../../pages/CreatePostPage/components/CreatePostForm/index";

interface Tag {
  id: string;
  title: string;
}

interface TagsFieldProps {
  control: Control<CreatePostFormData>;
  name: "tags";
  error?: string;
}

export const TagsField = ({ control, name, error }: TagsFieldProps) => {
  const { t } = useTranslation("feed", { keyPrefix: "postForm" });
  const { data: { items: availableTags } = { items: [] } } = useTags();

  return (
    <FormControl fullWidth component="fieldset" sx={commonStyles.formControl} error={!!error}>
      <FormLabel sx={commonStyles.formLabel}>{t("tags")}</FormLabel>
      <Controller
        name={name}
        control={control}
        render={({ field: { value, onChange } }) => (
          <Autocomplete
            sx={commonStyles.autocompleteStyles}
            forcePopupIcon={true}
            multiple
            options={availableTags || []}
            limitTags={3}
            freeSolo
            ListboxProps={{
              sx: { width: "393px" },
            }}
            value={[
              ...(availableTags?.filter((tag) => value?.old?.includes(tag.id.toString())) || []),
              ...(value?.new?.map((newTag) => ({ id: newTag, title: newTag })) || []),
            ]}
            onChange={(_, newValue) => {
              const existingTags = newValue
                .filter((tag): tag is Tag => typeof tag === "object" && tag !== null)
                .map((tag) => tag.id.toString());

              const newTags = newValue
                .filter((tag) => typeof tag === "string")
                .map((tag) => (tag as string).trim())
                .filter(Boolean);

              onChange({
                old: existingTags,
                new: newTags,
              });
            }}
            getOptionLabel={(option) => {
              if (typeof option === "string") return option;
              return option.title || "";
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                sx={commonStyles.textField}
                placeholder={value?.old?.length !== 0 || value?.new?.length !== 0 ? undefined : t("selectTags")}
                error={!!error}
              />
            )}
            popupIcon={<CreatePostAutocompleteIcon />}
            ChipProps={{
              variant: "outlined",
              sx: commonStyles.autocompleteChipStyles,
            }}
          />
        )}
      />
      {error && <FormHelperText error>{error}</FormHelperText>}
    </FormControl>
  );
};
